"use client";

import * as React from "react";

import { BaselineIcon, BoldIcon, Code2Icon, ItalicIcon, PaintBucketIcon, StrikethroughIcon, UnderlineIcon } from "lucide-react";
import { KEYS } from "platejs";
import { useEditorReadOnly } from "platejs/react";

import { AlignToolbarButton } from "./align-toolbar-button";
import { EmojiToolbarButton } from "./emoji-toolbar-button";
import { FontColorToolbarButton } from "./font-color-toolbar-button";
import { FontSizeToolbarButton } from "./font-size-toolbar-button";
import { RedoToolbarButton, UndoToolbarButton } from "./history-toolbar-button";
import { IndentToolbarButton, OutdentToolbarButton } from "./indent-toolbar-button";
import { InsertToolbarButton } from "./insert-toolbar-button";
import { LineHeightToolbarButton } from "./line-height-toolbar-button";
import { LinkToolbarButton } from "./link-toolbar-button";
import { BulletedListToolbarButton, NumberedListToolbarButton, TodoListToolbarButton } from "./list-toolbar-button";
import { MarkToolbarButton } from "./mark-toolbar-button";
import { TableToolbarButton } from "./table-toolbar-button";
import { ToggleToolbarButton } from "./toggle-toolbar-button";
import { ToolbarGroup } from "./toolbar";
import { TurnIntoToolbarButton } from "./turn-into-toolbar-button";
import { ResponsiveToolbarRow } from "./ResponsiveGroupsToolbar";
import { FullscreenToolbarButton } from "./full-screen-toolbar-button";

export function FixedToolbarButtons(props: {
  pluginConfig?: Record<string, boolean>;
  includeFullscreen?: boolean;
  isFullscreen?: boolean;
  onFullscreenChange?: () => void;
}) {
  const readOnly = useEditorReadOnly();
  if (readOnly)
    if (props.includeFullscreen !== false)
      return (
        <ToolbarGroup>
          <FullscreenToolbarButton isFullscreen={props.isFullscreen} onFullscreenChange={props.onFullscreenChange} />
        </ToolbarGroup>
      );
    else return null;

  const items = [
    {
      key: "history",
      node: (
        <ToolbarGroup>
          <UndoToolbarButton />
          <RedoToolbarButton />
        </ToolbarGroup>
      ),
    },
    {
      key: "insert",
      node: (
        <ToolbarGroup>
          <InsertToolbarButton />
          <TurnIntoToolbarButton />
          <FontSizeToolbarButton />
        </ToolbarGroup>
      ),
    },
    {
      key: "marks",
      node: (
        <ToolbarGroup>
          <MarkToolbarButton nodeType={KEYS.bold} tooltip="Bold (⌘+B)">
            <BoldIcon />
          </MarkToolbarButton>

          <MarkToolbarButton nodeType={KEYS.italic} tooltip="Italic (⌘+I)">
            <ItalicIcon />
          </MarkToolbarButton>

          <MarkToolbarButton nodeType={KEYS.underline} tooltip="Underline (⌘+U)">
            <UnderlineIcon />
          </MarkToolbarButton>

          <MarkToolbarButton nodeType={KEYS.strikethrough} tooltip="Strikethrough (⌘+⇧+M)">
            <StrikethroughIcon />
          </MarkToolbarButton>

          <MarkToolbarButton nodeType={KEYS.code} tooltip="Code (⌘+E)">
            <Code2Icon />
          </MarkToolbarButton>

          <FontColorToolbarButton nodeType={KEYS.color} tooltip="Text color">
            <BaselineIcon />
          </FontColorToolbarButton>

          <FontColorToolbarButton nodeType={KEYS.backgroundColor} tooltip="Background color">
            <PaintBucketIcon />
          </FontColorToolbarButton>
        </ToolbarGroup>
      ),
    },
    {
      key: "lists",
      node: (
        <ToolbarGroup>
          <AlignToolbarButton />
          <NumberedListToolbarButton />
          <BulletedListToolbarButton />
          <TodoListToolbarButton />
          <ToggleToolbarButton />
        </ToolbarGroup>
      ),
    },
    {
      key: "insert2",
      node: (
        <ToolbarGroup>
          <LinkToolbarButton />
          <TableToolbarButton />
          <EmojiToolbarButton />
        </ToolbarGroup>
      ),
    },
    {
      key: "indent",
      node: (
        <ToolbarGroup>
          <LineHeightToolbarButton />
          <OutdentToolbarButton />
          <IndentToolbarButton />
        </ToolbarGroup>
      ),
    },
    // {
    //   key: "fullScreen",
    //   node: (
    //     <ToolbarGroup>
    //       <FullscreenToolbarButton isFullscreen={props.isFullscreen} onFullscreenChange={props.onFullscreenChange} />
    //     </ToolbarGroup>
    //   ),
    // },
  ];

  return (
    <ResponsiveToolbarRow
      items={items}
      rightSlot={<FullscreenToolbarButton isFullscreen={props.isFullscreen} onFullscreenChange={props.onFullscreenChange} />} // optional pinned-right controls
      className="w-full"
    />
  );
}
